import type { SplatBlock } from "../format/format";
import { splatVertSrc as vertSrc, splatFragSrc as fragSrc } from "./shaders";
import { DepthSorter } from "./sorter";

// Splat data is stored in a 2D RGBA32F texture, 4 texels per splat:
//   t0: pos.xyz       , _
//   t1: shDc.rgb      , opacity
//   t2: scale.xyz     , _
//   t3: rot.xyzw
//
// Per-instance attribute is just one int — the splat index. Sort produces a
// Uint32Array permutation; we re-upload only the index buffer (4 bytes per
// splat) instead of the full interleaved data (56 bytes per splat).
// Reference: antimatter15/splat.
const TEXELS_PER_SPLAT = 4;
const FLOATS_PER_TEXEL = 4;
const FLOATS_PER_SPLAT = TEXELS_PER_SPLAT * FLOATS_PER_TEXEL; // 16
const DATA_TEX_WIDTH = 4096;

interface Slot {
  nodeId: number;
  start: number; // splat index in the data texture (0..capacitySplats)
  count: number;
  lastUsedFrame: number;
}

/**
 * Headless WebGL2 splat renderer. Owns its own program / VAO / textures /
 * buffers but does NOT own the canvas, RAF loop, or framebuffer state. The
 * host (e.g. a Three.js adapter) is expected to:
 *   - construct the renderer with the host's WebGL2 context
 *   - call `addNode` / `removeNode` as blocks arrive / evict
 *   - call `renderFrame(view, proj, viewportW, viewportH)` once per frame,
 *     after the host has cleared the framebuffer if needed
 *   - save & restore any GL state it cares about around the renderFrame call
 *     (we set program, VAO, blend func, depth-test off — host should reset
 *     after via `renderer.resetState()` in Three.js)
 *   - call `dispose()` on teardown
 */
export class Renderer {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private cornerVbo: WebGLBuffer;
  private indexVbo: WebGLBuffer; // per-instance int splatIndex
  private dataTex: WebGLTexture;
  private dataTexW: number;
  private dataTexH: number;
  private capacitySplats: number;

  private slotsByNode = new Map<number, Slot>();
  private slots: Slot[] = [];
  private freeRanges: Array<{ start: number; count: number }>;

  private positionsCpu: Float32Array; // CPU-side positions for sort, indexed by splat index
  private indexBufCpu: Uint32Array; // CPU-side sorted index, length = capacitySplats
  // Compacted active set, rebuilt only when the resident block set changes —
  // camera motion alone does not move a splat between slots.
  private activePositions: Float32Array;
  private activeIndices: Uint32Array;
  private activeDirty = true;
  private sorter = new DepthSorter();

  private uViewLoc: WebGLUniformLocation | null;
  private uProjLoc: WebGLUniformLocation | null;
  private uViewportLoc: WebGLUniformLocation | null;
  private uFocalLoc: WebGLUniformLocation | null;
  private uOrthographicLoc: WebGLUniformLocation | null;
  private uOrthoFocalLoc: WebGLUniformLocation | null;
  private uDataTexWLoc: WebGLUniformLocation | null;
  private uSplatDataLoc: WebGLUniformLocation | null;

  private frameNo = 0;

  constructor(gl: WebGL2RenderingContext, capacitySplats: number) {
    this.gl = gl;

    // Float texture sampling: needed for RGBA32F via texelFetch. WebGL2 supports
    // it without extension for sampling. Linear filtering would require
    // OES_texture_float_linear; we use NEAREST so that's fine.
    this.capacitySplats = capacitySplats;
    this.freeRanges = [{ start: 0, count: capacitySplats }];

    const totalTexels = capacitySplats * TEXELS_PER_SPLAT;
    this.dataTexW = DATA_TEX_WIDTH;
    this.dataTexH = Math.ceil(totalTexels / DATA_TEX_WIDTH);

    this.prog = compileProgram(gl, vertSrc, fragSrc);
    this.uViewLoc = gl.getUniformLocation(this.prog, "u_view");
    this.uProjLoc = gl.getUniformLocation(this.prog, "u_proj");
    this.uViewportLoc = gl.getUniformLocation(this.prog, "u_viewport");
    this.uFocalLoc = gl.getUniformLocation(this.prog, "u_focal");
    this.uOrthographicLoc = gl.getUniformLocation(this.prog, "u_orthographic");
    this.uOrthoFocalLoc = gl.getUniformLocation(this.prog, "u_orthoFocal");
    this.uDataTexWLoc = gl.getUniformLocation(this.prog, "u_dataTexW");
    this.uSplatDataLoc = gl.getUniformLocation(this.prog, "u_splatData");

    this.vao = mustCreate(gl, gl.createVertexArray());
    gl.bindVertexArray(this.vao);

    // Static unit-quad corners.
    this.cornerVbo = mustCreate(gl, gl.createBuffer());
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cornerVbo);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const aCorner = gl.getAttribLocation(this.prog, "a_corner");
    gl.enableVertexAttribArray(aCorner);
    gl.vertexAttribPointer(aCorner, 2, gl.FLOAT, false, 0, 0);

    // Per-instance int splatIndex.
    this.indexVbo = mustCreate(gl, gl.createBuffer());
    gl.bindBuffer(gl.ARRAY_BUFFER, this.indexVbo);
    gl.bufferData(gl.ARRAY_BUFFER, capacitySplats * 4, gl.DYNAMIC_DRAW);
    const aIdx = gl.getAttribLocation(this.prog, "a_splatIndex");
    gl.enableVertexAttribArray(aIdx);
    // Integer attribute (i32) — must use IPointer.
    gl.vertexAttribIPointer(aIdx, 1, gl.INT, 0, 0);
    gl.vertexAttribDivisor(aIdx, 1);

    // Splat data texture.
    this.dataTex = mustCreate(gl, gl.createTexture());
    gl.bindTexture(gl.TEXTURE_2D, this.dataTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      this.dataTexW,
      this.dataTexH,
      0,
      gl.RGBA,
      gl.FLOAT,
      null,
    );

    this.positionsCpu = new Float32Array(capacitySplats * 3);
    this.indexBufCpu = new Uint32Array(capacitySplats);
    this.activePositions = new Float32Array(capacitySplats * 3);
    this.activeIndices = new Uint32Array(capacitySplats);

    gl.bindVertexArray(null);
  }

  has(nodeId: number): boolean {
    return this.slotsByNode.has(nodeId);
  }

  touch(nodeId: number): void {
    const s = this.slotsByNode.get(nodeId);
    if (s) s.lastUsedFrame = this.frameNo;
  }

  bumpFrame(): void {
    this.frameNo++;
  }

  currentFrame(): number {
    return this.frameNo;
  }

  addNode(nodeId: number, block: SplatBlock): boolean {
    const n = block.splatCount;
    if (n === 0) return true;
    if (n > this.capacitySplats) {
      throw new Error(`Block too large for capacity: ${n} > ${this.capacitySplats}`);
    }
    if (this.slotsByNode.has(nodeId)) return true;

    // Debug: dump first splat of the first node we add
    if (this.slots.length === 0 && n > 0) {
      const log0 = (k: string, arr: ArrayLike<number>, off: number, count: number) => {
        const v: number[] = [];
        for (let i = 0; i < count; i++) v.push(arr[off + i] as number);
        return `${k}=[${v.map((x) => x.toFixed(4)).join(",")}]`;
      };
      const expScale = (i: number) =>
        Math.exp(block.scales[i] as number).toFixed(5);
      console.log(
        `[gsplat] renderer first-splat sample: ${log0("pos", block.positions, 0, 3)} ${log0("shDc", block.shDc, 0, 3)} ` +
          `opacity=${(block.opacities[0] as number).toFixed(3)} sigmoid=${(1 / (1 + Math.exp(-(block.opacities[0] as number)))).toFixed(3)} ` +
          `${log0("scale_log", block.scales, 0, 3)} scale_exp=[${expScale(0)},${expScale(1)},${expScale(2)}] ` +
          `${log0("rot", block.rotations, 0, 4)}`,
      );
    }

    let range = this.findFreeRange(n);
    while (!range) {
      const evicted = this.evictOne(this.frameNo);
      if (!evicted) return false;
      range = this.findFreeRange(n);
    }
    const allocStart = range.start;
    this.consumeRange(range, n);
    const slot: Slot = {
      nodeId,
      start: allocStart,
      count: n,
      lastUsedFrame: this.frameNo,
    };
    this.slotsByNode.set(nodeId, slot);
    this.slots.push(slot);

    this.uploadSlotData(slot, block);
    this.activeDirty = true;
    this.sorter.invalidate();
    return true;
  }

  removeNode(nodeId: number): void {
    const slot = this.slotsByNode.get(nodeId);
    if (!slot) return;
    this.slotsByNode.delete(nodeId);
    const idx = this.slots.indexOf(slot);
    if (idx >= 0) this.slots.splice(idx, 1);
    this.releaseRange(slot.start, slot.count);
    this.activeDirty = true;
    this.sorter.invalidate();
  }

  residentNodes(): number[] {
    return this.slots.map((s) => s.nodeId);
  }

  glContext(): WebGL2RenderingContext {
    return this.gl;
  }

  splatCount(): number {
    let total = 0;
    for (const s of this.slots) total += s.count;
    return total;
  }

  activeSlots(): number {
    return this.slots.length;
  }

  /**
   * Render the splat layer with the host's view/proj matrices and current
   * viewport. The host MUST:
   *   - have already cleared the framebuffer (or rendered other layers first)
   *   - have set the GL viewport to match (we don't call gl.viewport here)
   *
   * We do enable BLEND with ONE/ONE_MINUS_SRC_ALPHA and disable DEPTH_TEST
   * for the duration of this draw. The host is expected to invalidate its
   * GL state cache after (e.g. THREE.WebGLRenderer.resetState()).
   */
  renderFrame(
    viewMatrix: Float32Array,
    projMatrix: Float32Array,
    viewportW: number,
    viewportH: number,
  ): void {
    const gl = this.gl;
    if (this.slots.length === 0) return;
    const totalActive = this.splatCount();
    if (totalActive === 0) return;

    if (this.sorter.needsResort(viewMatrix)) {
      // Build the active set's positions (compact 0..totalActive*3) and a
      // parallel mapping to original splat indices. Sort permutes splat
      // indices, NOT the data — data lives in the texture.
      const activePositions = this.activePositions;
      const activeIndices = this.activeIndices;
      if (this.activeDirty) {
        let cursor = 0;
        for (const s of this.slots) {
          for (let i = 0; i < s.count; i++) {
            const splatIdx = s.start + i;
            activePositions[cursor * 3 + 0] = this.positionsCpu[splatIdx * 3 + 0]!;
            activePositions[cursor * 3 + 1] = this.positionsCpu[splatIdx * 3 + 1]!;
            activePositions[cursor * 3 + 2] = this.positionsCpu[splatIdx * 3 + 2]!;
            activeIndices[cursor] = splatIdx;
            cursor++;
          }
        }
        this.activeDirty = false;
      }
      const order = this.sorter.sort(activePositions, viewMatrix, totalActive);
      for (let i = 0; i < totalActive; i++) {
        this.indexBufCpu[i] = activeIndices[order[i]!]!;
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, this.indexVbo);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.indexBufCpu, 0, totalActive);
    }

    // Splat-specific GL state. The host should restore its own state after.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.DEPTH_TEST);

    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.dataTex);
    gl.uniform1i(this.uSplatDataLoc, 0);
    gl.uniform1i(this.uDataTexWLoc, this.dataTexW);
    gl.uniformMatrix4fv(this.uViewLoc, false, viewMatrix);
    gl.uniformMatrix4fv(this.uProjLoc, false, projMatrix);
    gl.uniform2f(this.uViewportLoc, viewportW, viewportH);
    // Detect projection mode from the matrix: a perspective projection has
    // proj[11] = -1 and proj[15] = 0; orthographic has proj[11] = 0 and
    // proj[15] = 1. Pixel-per-world-unit scale is computed differently:
    //   perspective:   focal = viewportH * proj[5] / 2  (1 / tan(fovY/2) × H/2)
    //   orthographic:  focalX = viewportW * proj[0] / 2
    //                  focalY = viewportH * proj[5] / 2
    const isOrtho = Math.abs(projMatrix[15]! - 1) < 1e-3 && Math.abs(projMatrix[11]!) < 1e-3;
    if (isOrtho) {
      const focalX = (viewportW * projMatrix[0]!) / 2;
      const focalY = (viewportH * projMatrix[5]!) / 2;
      gl.uniform1i(this.uOrthographicLoc, 1);
      gl.uniform2f(this.uOrthoFocalLoc, focalX, focalY);
      gl.uniform1f(this.uFocalLoc, 0);
    } else {
      const focal = (viewportH * projMatrix[5]!) / 2;
      gl.uniform1i(this.uOrthographicLoc, 0);
      gl.uniform2f(this.uOrthoFocalLoc, 0, 0);
      gl.uniform1f(this.uFocalLoc, focal);
    }
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, totalActive);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteBuffer(this.cornerVbo);
    gl.deleteBuffer(this.indexVbo);
    gl.deleteTexture(this.dataTex);
    gl.deleteVertexArray(this.vao);
    gl.deleteProgram(this.prog);
  }

  private uploadSlotData(slot: Slot, block: SplatBlock): void {
    const gl = this.gl;
    const packed = new Float32Array(slot.count * FLOATS_PER_SPLAT);
    const pos = this.positionsCpu;
    const offPos = slot.start * 3;
    for (let i = 0; i < slot.count; i++) {
      const off = i * FLOATS_PER_SPLAT;
      const px = block.positions[i * 3 + 0]!;
      const py = block.positions[i * 3 + 1]!;
      const pz = block.positions[i * 3 + 2]!;
      packed[off + 0] = px;
      packed[off + 1] = py;
      packed[off + 2] = pz;
      packed[off + 3] = 0;
      packed[off + 4] = block.shDc[i * 3 + 0]!;
      packed[off + 5] = block.shDc[i * 3 + 1]!;
      packed[off + 6] = block.shDc[i * 3 + 2]!;
      packed[off + 7] = block.opacities[i]!;
      packed[off + 8] = block.scales[i * 3 + 0]!;
      packed[off + 9] = block.scales[i * 3 + 1]!;
      packed[off + 10] = block.scales[i * 3 + 2]!;
      packed[off + 11] = 0;
      packed[off + 12] = block.rotations[i * 4 + 0]!;
      packed[off + 13] = block.rotations[i * 4 + 1]!;
      packed[off + 14] = block.rotations[i * 4 + 2]!;
      packed[off + 15] = block.rotations[i * 4 + 3]!;

      pos[offPos + i * 3 + 0] = px;
      pos[offPos + i * 3 + 1] = py;
      pos[offPos + i * 3 + 2] = pz;
    }

    const baseTexel = slot.start * TEXELS_PER_SPLAT;
    const totalTexels = slot.count * TEXELS_PER_SPLAT;
    gl.bindTexture(gl.TEXTURE_2D, this.dataTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    uploadLinearRange(gl, this.dataTexW, baseTexel, totalTexels, packed);
  }

  private findFreeRange(needed: number): { start: number; count: number } | null {
    for (const r of this.freeRanges) {
      if (r.count >= needed) return r;
    }
    return null;
  }

  private consumeRange(range: { start: number; count: number }, n: number): void {
    range.start += n;
    range.count -= n;
    if (range.count === 0) {
      const idx = this.freeRanges.indexOf(range);
      if (idx >= 0) this.freeRanges.splice(idx, 1);
    }
  }

  private releaseRange(start: number, count: number): void {
    this.freeRanges.push({ start, count });
    this.coalesceFreeRanges();
  }

  private coalesceFreeRanges(): void {
    this.freeRanges.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; count: number }> = [];
    for (const r of this.freeRanges) {
      const last = merged[merged.length - 1];
      if (last && last.start + last.count === r.start) {
        last.count += r.count;
      } else {
        merged.push({ start: r.start, count: r.count });
      }
    }
    this.freeRanges = merged;
  }

  private evictOne(excludeFrame?: number): boolean {
    if (this.slots.length === 0) return false;
    let oldestIdx = -1;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i]!;
      if (excludeFrame !== undefined && s.lastUsedFrame === excludeFrame) continue;
      if (oldestIdx < 0 || s.lastUsedFrame < this.slots[oldestIdx]!.lastUsedFrame) {
        oldestIdx = i;
      }
    }
    if (oldestIdx < 0) return false;
    const victim = this.slots[oldestIdx]!;
    this.slotsByNode.delete(victim.nodeId);
    this.slots.splice(oldestIdx, 1);
    this.releaseRange(victim.start, victim.count);
    // The active set just changed shape: both the compaction and the cached
    // sort order refer to slots that no longer exist.
    this.activeDirty = true;
    this.sorter.invalidate();
    return true;
  }
}

function uploadLinearRange(
  gl: WebGL2RenderingContext,
  texW: number,
  base: number,
  count: number,
  packed: Float32Array,
): void {
  let texelsLeft = count;
  let cursor = base;
  let srcOffsetFloats = 0;
  while (texelsLeft > 0) {
    const row = Math.floor(cursor / texW);
    const col = cursor % texW;
    const inThisRow = Math.min(texW - col, texelsLeft);
    const view = packed.subarray(srcOffsetFloats, srcOffsetFloats + inThisRow * 4);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, col, row, inThisRow, 1, gl.RGBA, gl.FLOAT, view);
    cursor += inThisRow;
    texelsLeft -= inThisRow;
    srcOffsetFloats += inThisRow * 4;
  }
}

function mustCreate<T>(gl: WebGL2RenderingContext, obj: T | null): T {
  if (obj === null) throw new Error(`WebGL allocation failed (gl error ${gl.getError()})`);
  return obj;
}

function compileProgram(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const v = compileShader(gl, gl.VERTEX_SHADER, vs);
  const f = compileShader(gl, gl.FRAGMENT_SHADER, fs);
  const p = mustCreate(gl, gl.createProgram());
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p) ?? "<no log>";
    gl.deleteProgram(p);
    throw new Error(`Program link failed: ${log}`);
  }
  gl.deleteShader(v);
  gl.deleteShader(f);
  return p;
}

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = mustCreate(gl, gl.createShader(type));
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? "<no log>";
    gl.deleteShader(sh);
    throw new Error(`Shader compile failed: ${log}`);
  }
  return sh;
}
